import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { LoadingAnimation, useResponsive } from '@sg/garnish';

import { ForgotToScan, useForgotToScanMachine } from '@order/features-forgot-to-scan';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEffect, useTrackEventEffect } from '@order/Telemetry';

import { NotFoundView, SignedOutView } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useNavigateBack } from '../../navigation';
import { useForgotToScanContent } from './hooks';

export const ForgotToScanScreen = () => {
  const isForgotToScanEnabled = useFeatureFlag('CELS-4258-forgot-to-scan-enabled');
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  const isLoggedIn = useIsLoggedIn();
  const { currentBreakpoint } = useResponsive();

  // ─── Machine ─────────────────────────────────────────────────────────

  const {
    isShowingLandingPage,
    isShowingForm,
    isSubmittingForm,
    hasPointsBeenIssued,
    isTimeRestrictionError,
    isReceiptAlreadyClaimedError,
    isTransactionNotFoundError,
    hasError,
    goBack,
    showForm,
    requestSubmission,
  } = useForgotToScanMachine();

  // ─── Navigation ──────────────────────────────────────────────────────

  const router = useRouter();
  const navigateBack = useNavigateBack();

  const goToPointsHistory = useCallback(() => {
    router.dismissTo('/rewards');
  }, [router]);

  const goToHome = useCallback(() => {
    router.dismissTo('/');
  }, [router]);

  const goToSupport = useCallback(() => {
    router.navigate('/chat');
  }, [router]);

  const closeModal = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  // ─── Content ─────────────────────────────────────────────────────────

  const {
    isFetchingContent,
    landingPageTitle,
    landingPageSubtitle,
    formTitle,
    formImage,
    formImageA11y,
    faqLink,
    errorMessage,
  } = useForgotToScanContent({
    isTimeRestrictionError,
    isReceiptAlreadyClaimedError,
    isTransactionNotFoundError,
  });

  // ─── Telemetry ─────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const handleGetStartedTapped = useCallback(() => {
    track('forgot-to-scan.get-started.tapped');
    showForm();
  }, [track, showForm]);

  const handleFaqTapped = useCallback(() => {
    track('forgot-to-scan.faq.tapped');
  }, [track]);

  const handleSubmissionSuccess = useCallback(
    (params: Parameters<typeof requestSubmission>[0]) => {
      track('forgot-to-scan.submit.tapped');
      requestSubmission(params);
    },
    [track, requestSubmission],
  );

  useTrackEffect('forgot-to-scan.viewed');

  useTrackEventEffect({
    name: 'forgot-to-scan.success.viewed',
    skip: !hasPointsBeenIssued,
  });

  useTrackEventEffect({
    name: 'forgot-to-scan.error.viewed',
    skip: !hasError,
    payload: { error: errorMessage },
  });

  // ─── Rendering ───────────────────────────────────────────────────────

  if (!isForgotToScanEnabled) {
    return <NotFoundView />;
  }

  if (!isLoggedIn) {
    return <SignedOutView redirectTo="/forgot-to-scan" />;
  }

  if (isFetchingContent) {
    return <LoadingAnimation />;
  }

  if (currentBreakpoint.isXS) {
    return (
      <ForgotToScan.FormContainer image={formImage} imageA11y={formImageA11y}>
        <ForgotToScan.Header goBack={isShowingForm ? goBack : undefined} onClose={closeModal} />

        {isShowingLandingPage ? (
          <ForgotToScan.Landing
            title={landingPageTitle}
            subtitle={landingPageSubtitle}
            onGetStartedPressed={handleGetStartedTapped}
            faqLink={faqLink}
            onFaqPressed={handleFaqTapped}
          />
        ) : null}

        {isShowingForm ? (
          <ForgotToScan.Form
            title={formTitle}
            faqLink={faqLink}
            onFaqPressed={handleFaqTapped}
            isSubmittingForm={isSubmittingForm}
            onSubmitForm={handleSubmissionSuccess}
          />
        ) : null}

        {hasPointsBeenIssued ? (
          <ForgotToScan.Success onViewPointsHistory={goToPointsHistory} onBack={goToHome} />
        ) : null}

        {hasError ? (
          <ForgotToScan.Error
            error={errorMessage}
            onSupport={goToSupport}
            onClose={closeModal}
            onBack={isTimeRestrictionError ? undefined : goBack}
          />
        ) : null}
      </ForgotToScan.FormContainer>
    );
  }

  return (
    <ForgotToScan.FormContainer
      image={formImage}
      imageA11y={formImageA11y}
      withNavbarSpacing={isNavbarV2Enabled}
    >
      {isShowingLandingPage || isShowingForm ? (
        <>
          <ForgotToScan.Landing
            title={landingPageTitle}
            subtitle={landingPageSubtitle}
            faqLink={faqLink}
            onFaqPressed={handleFaqTapped}
            onGetStartedPressed={handleGetStartedTapped}
          />

          <ForgotToScan.Form
            title={formTitle}
            faqLink={faqLink}
            onFaqPressed={handleFaqTapped}
            isSubmittingForm={isSubmittingForm}
            onSubmitForm={requestSubmission}
          />
        </>
      ) : null}

      {hasPointsBeenIssued ? (
        <ForgotToScan.Success onViewPointsHistory={goToPointsHistory} onBack={goToHome} />
      ) : null}

      {hasError ? (
        <ForgotToScan.Error
          error={errorMessage}
          onSupport={goToSupport}
          onClose={closeModal}
          onBack={isTimeRestrictionError ? undefined : goBack}
        />
      ) : null}
    </ForgotToScan.FormContainer>
  );
};
